import React from 'react';
import { observer } from 'mobx-react';

import { UuidableName, LocationAnswerType } from 'utils/api/types';
import { EntityModules, FLOATING_TIMEZONE, ProtectorType } from 'core/constants';

import { AttachmentType } from 'utils/api/modules/incidents/incidents.types';

import EntitySearchField from 'components/EntitySearchField';
import { StyledTextField } from 'components/Input/styles';

import CustomFieldDisplayValue from 'components/SharedFormComponents/CustomFieldDisplayValue';
import CustomTimePicker from 'components/SharedFormComponents/CustomTimePicker';
import CustomDateTimePicker from 'components/SharedFormComponents/CustomDateTimePicker';
import CustomNumberInputField from 'components/SharedFormComponents/CustomNumberInputField';
import CustomTextInputField from 'components/SharedFormComponents/CustomTextInputField';
import CustomUserDropdownField from 'components/SharedFormComponents/CustomUserDropdownField';
import CustomDatePicker from 'components/SharedFormComponents/CustomDatePicker';
import CustomLocationTextInput from 'components/SharedFormComponents/CustomLocationTextInput';

import Logger from 'utils/logger';
import { readerDayKey } from 'utils/formatters/timezone';

import { timezoneStore } from 'stores/Timezone';

import AuditsLookupField from 'components/Audits/AuditsLookupField';
import { optionSolidTones } from 'components/Audits/choiceTone';
import { LookupAnswerEntry } from 'components/Audits/lookupRecords';

import { isAuditsSubmission } from 'containers/EntitiesFormSubmission/config';

import EntitiesDropDownField from '../EntitiesDropDownField';
import EntitiesInlineChoiceField, { INLINE_OPTION_LIMIT } from '../EntitiesInlineChoiceField';
import EntitiesYesNoNaField from '../EntitiesYesNoNaField';
import { EntitiesFormFieldProps } from './types';
import CustomAttachmentField from '../CustomAttachmentField';
import CustomSignatureField from '../CustomSignatureField';

export const EntitiesFormField = observer(function EntitiesFormField({
  type,
  entityType,
  value,
  onChange,
  description,
  items,
  requiredNotFilled,
  lookupType,
  fieldKey,
  errorMessage,
  noFutureDate,
  multiple,
  userFilter,
}: EntitiesFormFieldProps): React.ReactElement {
  const isAudits = isAuditsSubmission(entityType ?? null);
  const pinned = timezoneStore.enabled;

  const commonTextInputProps = {
    value,
    description,
    onChange,
    requiredNotFilled,
    errorMessage,
  };

  const handleDropdownChange = (newValue: string[]): void => {
    onChange(newValue);
  };

  const commonDropDownProps = {
    type,
    value: (value as string[]) || [],
    items,
    description,
    onChange: handleDropdownChange,
    requiredNotFilled,
  };

  // Zero options falls back to the dropdown: an empty inline group would render nothing at all.
  const optionCount = items?.length || 0;
  const inlineChoice = isAudits && optionCount > 0 && optionCount <= INLINE_OPTION_LIMIT;

  const handleUserChange = (val: UuidableName | null): void => {
    onChange(val);
  };

  const handleLookupChange = (_field: string, val: UuidableName | UuidableName[] | null): void => {
    if (Array.isArray(val)) {
      onChange(val.map((v) => v.uuid));
      return;
    }
    onChange(val);
  };

  switch (type) {
    case ProtectorType.ShortText:
      return <CustomTextInputField {...commonTextInputProps} />;
    case ProtectorType.LongText:
      return <CustomTextInputField {...commonTextInputProps} multiline />;
    case ProtectorType.SingleChoice:
    case ProtectorType.MultiChoice:
      if (inlineChoice) {
        return (
          <EntitiesInlineChoiceField
            type={type}
            value={(value as string[]) || []}
            items={items}
            onChange={handleDropdownChange}
            requiredNotFilled={requiredNotFilled}
            errorMessage={errorMessage}
            description={description}
            optionTone={optionSolidTones(items)}
          />
        );
      }
      return <EntitiesDropDownField {...commonDropDownProps} />;
    case ProtectorType.YesNoNa:
      return (
        <EntitiesYesNoNaField
          value={(value as string[]) || []}
          items={items}
          onChange={handleDropdownChange}
          requiredNotFilled={requiredNotFilled}
          errorMessage={errorMessage}
          description={description}
        />
      );
    case ProtectorType.Number:
      return (
        <CustomNumberInputField
          value={value}
          description={description}
          onChange={onChange}
          requiredNotFilled={requiredNotFilled}
          errorMessage={errorMessage}
        />
      );
    case ProtectorType.Date:
      return (
        <CustomDatePicker
          value={value}
          description={description}
          onChange={onChange}
          requiredNotFilled={requiredNotFilled}
          errorMessage={errorMessage}
          size="medium"
          noFutureDate={noFutureDate}
          tzFree={isAudits}
          today={pinned ? readerDayKey(Date.now()) : undefined}
        />
      );
    case ProtectorType.Time:
      return (
        <CustomTimePicker
          value={value}
          description={description}
          onChange={onChange}
          requiredNotFilled={requiredNotFilled}
          errorMessage={errorMessage}
          size="medium"
          tzFree={isAudits}
          timezone={pinned && !isAudits ? FLOATING_TIMEZONE : undefined}
        />
      );
    case ProtectorType.DateTime:
      return (
        <CustomDateTimePicker
          value={value}
          description={description}
          onChange={onChange}
          requiredNotFilled={requiredNotFilled}
          errorMessage={errorMessage}
          size="medium"
          noFutureDate={noFutureDate}
          timezone={pinned ? timezoneStore.readerZone : undefined}
        />
      );
    case ProtectorType.Location:
      return (
        <CustomLocationTextInput
          onChange={onChange}
          location={value ? (value as LocationAnswerType).address : undefined}
          requiredNotFilled={requiredNotFilled}
        />
      );
    case ProtectorType.Lookup:
      // A template saved before the builder required "Related to" has nothing to search.
      if (!lookupType) {
        return <StyledTextField fullWidth disabled placeholder="This question has no record type" />;
      }
      if (multiple) {
        return (
          <AuditsLookupField
            module={lookupType as EntityModules}
            value={Array.isArray(value) ? (value as LookupAnswerEntry[]) : []}
            onChange={(records) => onChange(records)}
            requiredNotFilled={requiredNotFilled}
            errorMessage={errorMessage}
            fieldKey={fieldKey}
          />
        );
      }
      return (
        <EntitySearchField
          field="lookup"
          updateField={handleLookupChange}
          data={value ? (value as UuidableName) : null}
          module={lookupType as EntityModules}
        />
      );
    case ProtectorType.User:
      return (
        <CustomUserDropdownField
          value={value as UuidableName | null}
          description={description}
          onChange={handleUserChange}
          requiredNotFilled={requiredNotFilled}
          errorMessage={errorMessage}
          licenses={userFilter?.licenses}
          userRole={userFilter?.role}
        />
      );
    case ProtectorType.RiskFactor:
      // A rating is edited through the record's scoring panel, which owns both standard fields. A
      // layout saved before the section was fixed can still split the pair, so this defers to the
      // read renderer rather than erroring, and inherits its empty state.
      return <CustomFieldDisplayValue type={type} value={value ?? null} />;
    case ProtectorType.Attachment:
      return (
        <CustomAttachmentField
          value={value as AttachmentType[] | undefined}
          onChange={onChange}
          description={description}
          requiredNotFilled={requiredNotFilled}
          fieldKey={fieldKey}
          errorMessage={errorMessage}
        />
      );
    case ProtectorType.Signature:
      return (
        <CustomSignatureField
          value={value as string | undefined}
          onChange={onChange}
          requiredNotFilled={requiredNotFilled}
          errorMessage={errorMessage}
        />
      );
    default:
      Logger.error(`EntitiesFormField: Type not supported: ${type}`);
      return <p>Type not supported</p>;
  }
});

export default EntitiesFormField;
